/**
 * Вспомогательные функции
 * Вынесено из legacy-app.js для модульности
 */

/**
 * Нормализует строку (удаляет лишние пробелы)
 * 
 * Заменяет множественные пробелы на один и обрезает пробелы по краям.
 * 
 * @param {string|null|undefined} str - Строка для нормализации
 * @returns {string} Нормализованная строка или пустая строка, если входное значение falsy
 * 
 * @example
 * normalizeString("  hello   world  ") // "hello world"
 * normalizeString(null) // ""
 */
export function normalizeString(str) {
  return str ? String(str).replace(/\s+/g, " ").trim() : "";
}

/**
 * Находит существо в коллекции по ID
 * 
 * Ищет существо сначала по userCreatureId, затем по creatureId.
 * 
 * @param {Array} collection - Коллекция существ
 * @param {number|null} creatureId - ID базового существа
 * @param {number|null} userCreatureId - ID пользовательского существа
 * @returns {Object|null} Найденное существо или null
 * 
 * @example
 * const creature = findCreatureInCollection(collection, 123, 456);
 */
export function findCreatureInCollection(collection, creatureId, userCreatureId) {
  if (!Array.isArray(collection) || collection.length === 0) {
    return null;
  }

  if (userCreatureId != null) {
    const found = collection.find((creature) => Number(creature.id) === userCreatureId);
    if (found) return found;
  }

  if (creatureId != null) {
    return collection.find(
      (creature) => Number(creature.creatureId ?? creature.creature_id) === creatureId,
    );
  }

  return null;
}

/**
 * @typedef {Object} CollectionDataEnsurerParams
 * @property {Function} safeFetch - Функция безопасного fetch
 * @property {Function} buildAuthHeaders - Функция построения заголовков авторизации
 * @property {Function} getApiUrl - Функция получения URL API
 * @property {Function} getCollection - Функция получения коллекции
 * @property {Function} setCollection - Функция установки коллекции
 * @property {Function} getCurrentUser - Функция получения текущего пользователя
 * @property {Function} setCurrentUser - Функция установки текущего пользователя
 */

/**
 * Обеспечивает наличие данных коллекции
 * 
 * Создает функцию, которая проверяет наличие коллекции и загружает её,
 * если она пуста, через API endpoint /user/me.
 * 
 * @param {CollectionDataEnsurerParams} params - Параметры
 * @returns {Function} Функция ensureCollectionData для вызова
 * 
 * @example
 * const ensureCollectionData = createCollectionDataEnsurer({
 *   safeFetch: safeFetchWithEnhancements,
 *   buildAuthHeaders,
 *   getApiUrl: () => apiUrl,
 *   getCollection: () => Y,
 *   setCollection: (collection) => { Y = collection; },
 *   getCurrentUser: () => d,
 *   setCurrentUser: (user) => { d = user; },
 * });
 * await ensureCollectionData();
 */
export function createCollectionDataEnsurer({
  safeFetch,
  buildAuthHeaders,
  getApiUrl,
  getCollection,
  setCollection,
  getCurrentUser,
  setCurrentUser,
}) {
  /**
   * Обеспечивает наличие данных коллекции
   * 
   * @returns {Promise<void>}
   */
  return async function ensureCollectionData() {
    const collection = getCollection();
    if (collection.length > 0) {
      return;
    }

    try {
      const apiUrl = getApiUrl();
      const response = await safeFetch(`${apiUrl}/user/me`, {
        headers: buildAuthHeaders(),
      });

      if (response.success && response.data?.success) {
        setCollection(response.data.collection || []);
        if (response.data.user) {
          setCurrentUser(response.data.user);
        }
      }
    } catch (error) {
      console.error("ensureCollectionData error", error);
    }
  };
}














